/**
 * Browser-side E2EE helpers (Web Crypto).
 * Master key is derived from the recovery key (PBKDF2). Server only stores a SHA-256 hash of the recovery key.
 */

const PBKDF2_ITERS = 150_000;
const KDF_SALT_LABEL = 'cloudtransfer-e2ee-v1';

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function toB64(u8: Uint8Array): string {
  let s = '';
  u8.forEach(b => (s += String.fromCharCode(b)));
  return btoa(s);
}

export async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', utf8(input));
  return [...new Uint8Array(buf)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function generateRecoveryKey(): string {
  const raw = new Uint8Array(32);
  crypto.getRandomValues(raw);
  return toB64(raw);
}

export async function deriveMasterKey(recoveryKey: string): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    utf8(recoveryKey),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey'],
  );
  const salt = utf8(KDF_SALT_LABEL);
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERS,
      hash: 'SHA-256',
    },
    keyMaterial,
    {name: 'AES-GCM', length: 256},
    true,
    ['encrypt', 'decrypt'],
  );
}

export async function exportMasterJwk(key: CryptoKey): Promise<string> {
  const jwk = await crypto.subtle.exportKey('jwk', key);
  return JSON.stringify(jwk);
}

export async function importMasterJwk(json: string): Promise<CryptoKey> {
  const jwk = JSON.parse(json);
  return crypto.subtle.importKey(
    'jwk',
    jwk,
    {name: 'AES-GCM', length: 256},
    true,
    ['encrypt', 'decrypt'],
  );
}

export type E2eeUploadMeta = {
  encryptionMode: 'e2ee';
  e2eeIv: string;
  e2eeWrappedKey: string;
  clientMime: string;
};

/**
 * Encrypt file bytes; returns ciphertext blob + metadata for Tus/S3 pipelines.
 */
export async function encryptFileForUpload(
  file: File,
  masterKey: CryptoKey,
  originalMime: string,
): Promise<{blob: Blob; meta: E2eeUploadMeta}> {
  const plain = await file.arrayBuffer();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const fileKey = await crypto.subtle.generateKey(
    {name: 'AES-GCM', length: 256},
    true,
    ['encrypt', 'decrypt'],
  );
  const ciphertext = await crypto.subtle.encrypt(
    {name: 'AES-GCM', iv},
    fileKey,
    plain,
  );
  const rawFileKey = new Uint8Array(await crypto.subtle.exportKey('raw', fileKey));
  const wrapIv = crypto.getRandomValues(new Uint8Array(12));
  const wrappedRaw = await crypto.subtle.encrypt(
    {name: 'AES-GCM', iv: wrapIv},
    masterKey,
    rawFileKey,
  );
  const meta: E2eeUploadMeta = {
    encryptionMode: 'e2ee',
    e2eeIv: toB64(iv),
    e2eeWrappedKey: `${toB64(wrapIv)}:${toB64(new Uint8Array(wrappedRaw))}`,
    clientMime: originalMime,
  };
  return {
    blob: new Blob([ciphertext], {type: 'application/octet-stream'}),
    meta,
  };
}

/**
 * Decrypt ciphertext produced by {@link encryptFileForUpload}.
 */
export async function decryptE2eeFile(
  ciphertext: ArrayBuffer,
  masterKey: CryptoKey,
  ivB64: string,
  wrappedKeyB64: string,
): Promise<ArrayBuffer> {
  const parts = wrappedKeyB64.split(':');
  if (parts.length !== 2) {
    throw new Error('Invalid E2EE wrapped key format');
  }
  const [wrapIvB64, wrappedCtB64] = parts;
  const wrapIv = Uint8Array.from(atob(wrapIvB64), c => c.charCodeAt(0));
  const wrappedBytes = Uint8Array.from(atob(wrappedCtB64), c =>
    c.charCodeAt(0),
  );
  const rawFileKey = await crypto.subtle.decrypt(
    {name: 'AES-GCM', iv: wrapIv},
    masterKey,
    wrappedBytes,
  );
  const fileKey = await crypto.subtle.importKey(
    'raw',
    rawFileKey,
    {name: 'AES-GCM', length: 256},
    false,
    ['decrypt'],
  );
  const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
  return crypto.subtle.decrypt({name: 'AES-GCM', iv}, fileKey, ciphertext);
}

export async function generateRsaKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt'],
  );
}

export async function exportRsaPublicJwk(key: CryptoKey): Promise<string> {
  return JSON.stringify(await crypto.subtle.exportKey('jwk', key));
}

export async function importRsaPublicJwk(json: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    JSON.parse(json),
    {name: 'RSA-OAEP', hash: 'SHA-256'},
    true,
    ['encrypt'],
  );
}

export async function exportRsaPrivateJwk(key: CryptoKey): Promise<string> {
  return JSON.stringify(await crypto.subtle.exportKey('jwk', key));
}

export async function importRsaPrivateJwk(json: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    JSON.parse(json),
    {name: 'RSA-OAEP', hash: 'SHA-256'},
    true,
    ['decrypt'],
  );
}

export async function unwrapFileKeyFromMaster(
  masterKey: CryptoKey,
  wrappedKeyB64: string,
): Promise<CryptoKey> {
  const parts = wrappedKeyB64.split(':');
  if (parts.length !== 2) {
    throw new Error('Invalid E2EE wrapped key format');
  }
  const [wrapIvB64, wrappedCtB64] = parts;
  const wrapIv = Uint8Array.from(atob(wrapIvB64), c => c.charCodeAt(0));
  const wrappedBytes = Uint8Array.from(atob(wrappedCtB64), c =>
    c.charCodeAt(0),
  );
  const rawFileKey = await crypto.subtle.decrypt(
    {name: 'AES-GCM', iv: wrapIv},
    masterKey,
    wrappedBytes,
  );
  return crypto.subtle.importKey(
    'raw',
    rawFileKey,
    {name: 'AES-GCM', length: 256},
    true,
    ['encrypt', 'decrypt'],
  );
}

export async function wrapFileKeyForRecipient(
  fileKey: CryptoKey,
  recipientPublicKey: CryptoKey,
): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', fileKey);
  const wrapped = await crypto.subtle.encrypt(
    {name: 'RSA-OAEP'},
    recipientPublicKey,
    raw,
  );
  return toB64(new Uint8Array(wrapped));
}

export async function unwrapFileKeyForRecipient(
  wrappedKeyB64: string,
  recipientPrivateKey: CryptoKey,
): Promise<CryptoKey> {
  const wrappedBytes = Uint8Array.from(atob(wrappedKeyB64), c =>
    c.charCodeAt(0),
  );
  const raw = await crypto.subtle.decrypt(
    {name: 'RSA-OAEP'},
    recipientPrivateKey,
    wrappedBytes,
  );
  return crypto.subtle.importKey(
    'raw',
    raw,
    {name: 'AES-GCM', length: 256},
    true,
    ['decrypt'],
  );
}

export async function decryptE2eeFileWithFileKey(
  ciphertext: ArrayBuffer,
  fileKey: CryptoKey,
  ivB64: string,
): Promise<ArrayBuffer> {
  const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
  return crypto.subtle.decrypt({name: 'AES-GCM', iv}, fileKey, ciphertext);
}

export function guessMimeFromFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    txt: 'text/plain',
    zip: 'application/zip',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };
  return map[ext] || 'application/octet-stream';
}

